/**
 * Lift2Fit — the admin surface's shared vocabulary.
 *
 * The admin is the founder's desk tool, covering both the lifter product and
 * the CRM. It reads accounts, businesses, usage and money — aggregates only —
 * and every write is deliberate, confirmed and audited. These helpers are the
 * presentation layer for that: plan/role/tier labels, a confirmation panel and
 * two small stat primitives. Nothing here talks to the API.
 */
import type { ReactNode } from 'react';
import type { CoachPlan, Role } from '@/lib/api';
import { cn, formatCount } from '@/lib/format';
import { Button } from '@/components/ui';
import { MiniStat } from '../crm/shared';
import type { ChipTone } from '../components';

/** The eyebrow every admin screen carries, so the third surface reads apart. */
export const ADMIN_EYEBROW = 'Platform admin';

/** Re-exported from `@/lib/format` so every surface groups counts identically. */
export { formatCount };

export function coachPlanLabel(plan: CoachPlan): string {
  switch (plan) {
    case 'coach':
      return 'Coach';
    case 'gym':
      // The stored enum value stays `gym`; the product now calls the tier Gyms,
      // and `gym_plus` is Gyms Plus. A gym is a coach — there is no gym role.
      return 'Gyms';
    case 'gym_plus':
      return 'Gyms Plus';
    case 'scale':
      return 'Scale';
    default:
      return 'Free';
  }
}

/** The plan's client/coach ceiling, worded as a cap so tiers never read as overlapping ranges. */
export function coachPlanSize(plan: CoachPlan): string {
  switch (plan) {
    case 'coach':
      return 'Up to 10 clients · own clients only';
    case 'gym':
      return 'Up to 20 clients · 10 internal coaches';
    case 'gym_plus':
      return 'Up to 100 clients · 20 internal coaches';
    case 'scale':
      return 'Unlimited · negotiated';
    default:
      return 'Up to 1 client';
  }
}

export function seatLimitLabel(limit: number | null): string {
  return limit === null ? 'unlimited' : formatCount(limit);
}

export function tierLabel(tier: 'basic' | 'premium' | 'plan_once'): string {
  if (tier === 'premium') return 'Premium';
  if (tier === 'plan_once') return 'One-off (legacy)';
  return 'Free';
}

const ROLE_TONES: Readonly<Record<Role, ChipTone>> = {
  admin: 'danger',
  coach: 'accent',
  trainer: 'info',
  lifter: 'neutral',
};

export function roleTone(role: Role): ChipTone {
  return ROLE_TONES[role];
}

/**
 * The product word for a role. `trainer` is an internal legacy database role —
 * existing rows and `trainee_links.trainer_id` — and never a product role, so
 * the admin surface names it honestly without printing the legacy word.
 */
export function roleLabel(role: Role): string {
  switch (role) {
    case 'coach':
      return 'Coach';
    case 'admin':
      return 'Admin';
    case 'lifter':
      return 'Lifter';
    default:
      return 'Legacy roster';
  }
}

/** A labelled number for the compact dashboards. */
export function AdminStat({
  label,
  value,
  hint,
  tone = 'text-ink',
  valueTitle,
  valueTestId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: string;
  /** The exact figure, when `value` is a compacted summary rendering. */
  valueTitle?: string;
  valueTestId?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 border border-hairline bg-surface p-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      {/* `min-w-0` + `break-words`: the value wraps inside the border rather
          than escaping it; never clipped with overflow. */}
      <span
        data-testid={valueTestId}
        title={valueTitle}
        className={cn('l2f-tabular min-w-0 break-words font-mono text-[22px] font-bold leading-none', tone)}
      >
        {value}
      </span>
      {hint ? <span className="text-[11px] text-ink-muted">{hint}</span> : null}
    </div>
  );
}

/**
 * The confirmation gate every admin write passes through: it states exactly
 * what will be written before the button that writes it is reachable.
 */
export function ChangeConfirm({
  summary,
  busy,
  confirmLabel = 'Confirm change',
  onConfirm,
  onCancel,
  testId = 'admin-change-confirm',
}: {
  summary: string;
  busy: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      role="alertdialog"
      aria-label="Confirm the admin change"
      className="flex flex-col gap-3 border-l-2 border-warning bg-page-subtle p-3"
    >
      <p className="text-[13px] text-ink">
        This will write: <span className="font-bold">{summary}</span>
      </p>
      <p className="text-[12px] text-ink-muted">
        The audit trail records who made the change, the account, the time and the before/after values.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={onConfirm} data-testid="admin-change-confirm-button">
          {busy ? 'Writing…' : confirmLabel}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** A small labelled value row, for the account detail. */
export function AdminRow({ label, value, tone, valueTitle }: { label: string; value: ReactNode; tone?: string; valueTitle?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-hairline py-2 last:border-b-0">
      <span className="text-[12px] uppercase tracking-[0.08em] text-ink-muted">{label}</span>
      <span title={valueTitle} className={cn('l2f-tabular min-w-0 break-words text-right font-mono text-[14px] font-bold', tone ?? 'text-ink')}>
        {value}
      </span>
    </div>
  );
}

export { MiniStat };
